// Tom Select initialization
//
// Purpose: convert every <select class="form-control|form-select"> to a Tom
// Select widget on each Turbo page load. Skip elements that are already
// initialized or explicitly disabled.
//
// Plugins applied to every select:
//   remove_button    — × on each selected item; clears single-select selection
//   dropdown_input   — moves the search field inside the dropdown itself
//
// dropdown_header (non-selectable title at top of dropdown) is available but commented out.
//
// Multi-selects additionally get:
//   clear_button     — × at the right edge to clear all selections at once
//   checkbox_options — checkboxes next to each option in the dropdown
//
// optgroup_columns (side-by-side column layout) is available but commented out.
//
// turbo:before-cache destroys instances so the snapshot contains plain
// <select> elements (safe for cache restore / back-button). TomSelect's
// destroy() calls revertSettings(), which resets the <select> to its original
// server-rendered state — discarding the user's current selection. We save
// values before destroying, then write them back to the native <select> so
// the Turbo snapshot captures the correct selection. This makes back-button
// restoration visits (which replay the snapshot without a server request)
// restore the correct filter state. The WeakMap also restores values after
// re-init on turbo:frame-load as a belt-and-suspenders fallback.
//
// Green border (border border-success) is applied server-side when a filter
// has a value. We sync it dynamically on change and after silent value
// restoration so clearing a selection also removes the visual indicator.

import TomSelect from "tom-select";

const savedValues = new WeakMap();

// Mirrors whether a select has a value onto the TomSelect wrapper and the
// underlying <select> element. The underlying element is hidden by TomSelect
// but Capybara's visible: false selector still reads its class list.
function syncBorder(ts, value) {
  const hasValue = Array.isArray(value) ? value.length > 0 : value !== "";
  ts.wrapper.classList.toggle("border", hasValue);
  ts.wrapper.classList.toggle("border-success", hasValue);
  ts.input.classList.toggle("border", hasValue);
  ts.input.classList.toggle("border-success", hasValue);
}

// Wires inactive-user filtering for a single user-select input-group.
// Stores the TomSelect instance and inactive-option snapshot directly on the
// toggle button element so the click handler needs no DOM traversal or WeakMap lookup.
function setupUserSelectToggle(ts, el) {
  // HAML 6 renders `data: { inactive: true }` as a valueless boolean attribute
  // (dataset value = ""), not "true". Check key presence rather than value.
  const inactive = Object.values(ts.options)
    .filter((opt) => "inactive" in opt)
    .map((opt) => ({ ...opt }));

  // Hide non-selected inactive options initially.
  inactive.forEach((opt) => {
    if (!ts.items.includes(String(opt.value))) ts.removeOption(opt.value, true);
  });
  if (inactive.length) ts.refreshOptions(false);

  // The button is a sibling of the select inside the same .input-group.
  const btn = el.parentElement?.querySelector("[data-user-toggle]");
  if (!btn) return;

  // Attach what the click handler needs directly to the button node.
  // This avoids WeakMap key-identity issues entirely.
  btn._phqTs = ts;
  btn._phqInactive = inactive;
}

// Delegates inactive-user toggle clicks so dynamically replaced selects keep working.
document.addEventListener("click", function (e) {
  const btn = e.target.closest("[data-user-toggle]");
  if (!btn) return;

  const ts = btn._phqTs;
  const inactive = btn._phqInactive;
  if (!ts || !inactive || !inactive.length) return;

  const showAll = btn.dataset.showAll !== "true";
  btn.dataset.showAll = showAll;

  // FontAwesome SVG+JS replaces <i> tags with <svg> so we can't manipulate the
  // icon's class directly. Instead we toggle two <span> wrappers that FA ignores.
  const iconOff = btn.querySelector("[data-phq-icon='inactive-off']");
  const iconOn = btn.querySelector("[data-phq-icon='inactive-on']");

  if (showAll) {
    inactive.forEach((opt) => {
      if (!ts.options[opt.value]) ts.addOption(opt);
    });
    ts.refreshOptions(false);
    btn.classList.replace("btn-outline-secondary", "btn-outline-warning");
    btn.title = "Hide inactive users";
    iconOff?.classList.add("d-none");
    iconOn?.classList.remove("d-none");
  } else {
    inactive.forEach((opt) => {
      if (!ts.items.includes(String(opt.value)))
        ts.removeOption(opt.value, true);
    });
    ts.refreshOptions(false);
    btn.classList.replace("btn-outline-warning", "btn-outline-secondary");
    btn.title = "Show inactive users";
    iconOff?.classList.remove("d-none");
    iconOn?.classList.add("d-none");
  }
});

// Save current values and destroy TomSelect instances before Turbo snapshots plain selects.
document.addEventListener("turbo:before-cache", function () {
  document
    .querySelectorAll("select.form-control, select.form-select")
    .forEach((el) => {
      if (el.tomselect) {
        // Prefer the native element's actual selected value over TomSelect's
        // internal state: external changes (e.g. Capybara) update the native
        // <select> without going through TomSelect's API, so ts.getValue() can
        // be stale. Save before destroy() since destroy() calls revertSettings()
        // which resets the element's classes and may reset selected state.
        const nativeVal = el.multiple
          ? Array.from(el.selectedOptions, (o) => o.value).filter(
              (v) => v !== "",
            )
          : el.value;
        const hasNativeVal = Array.isArray(nativeVal)
          ? nativeVal.length > 0
          : nativeVal !== "";
        const val = hasNativeVal ? nativeVal : el.tomselect.getValue();
        savedValues.set(el, val);
        el.tomselect.destroy();
        // Write the value back to the native <select> so the Turbo snapshot
        // captures the correct selection. destroy() reverts the element to its
        // original server-rendered state (losing the user's current choice);
        // without this, back-button restoration replays an empty snapshot.
        if (el.multiple) {
          const valArr = Array.isArray(val) ? val : [val];
          Array.from(el.options).forEach((opt) => {
            opt.selected = valArr.includes(opt.value);
          });
        } else {
          el.value = Array.isArray(val) ? (val[0] ?? "") : val;
        }
      }
    });
});

// Converts eligible select elements into TomSelect widgets and restores saved values.
function initTomSelects() {
  document
    .querySelectorAll("select.form-control, select.form-select")
    .forEach((el) => {
      if (el.tomselect) return;
      if (el.disabled) return;
      if (el.dataset.noTomselect) return;

      const isMultiple = el.multiple;
      const hasOptgroups = !!el.querySelector("optgroup");

      // Read placeholder text from the blank option or the placeholder attribute.
      // We use allowEmptyOption: false so TomSelect does not render the blank
      // option as a selectable item — remove_button handles clearing instead.
      const emptyOption = el.querySelector('option[value=""]');
      const placeholderText =
        el.getAttribute("placeholder") ||
        emptyOption?.textContent?.trim() ||
        "";

      const plugins = {
        remove_button: { title: "Remove" },
        // dropdown_header: { title: placeholderText },
        dropdown_input: {},
      };
      if (isMultiple) {
        plugins.clear_button = {};
        plugins.checkbox_options = {};
      }
      // if (hasOptgroups) {
      //   plugins.optgroup_columns = {};
      // }

      // Inactive user options are styled in muted italic when revealed.
      const isUserSelect = el.hasAttribute("data-user-select");
      const userRender = isUserSelect
        ? {
            option: (data, escape) => {
              const cls =
                "inactive" in data ? ' class="text-muted fst-italic"' : "";
              return `<div${cls}>${escape(data.text)}</div>`;
            },
            item: (data, escape) => {
              const cls =
                "inactive" in data ? ' class="text-muted fst-italic"' : "";
              return `<div${cls}>${escape(data.text)}</div>`;
            },
          }
        : {};

      const ts = new TomSelect(el, {
        plugins,
        allowEmptyOption: false,
        placeholder: placeholderText,
        maxOptions: null,
        dropdownParent: "body",
        render: userRender,
        // Scrolls the dropdown to the selected item after TomSelect opens.
        onDropdownOpen: function () {
          var self = this;
          setTimeout(function () {
            if (self.items.length > 0) {
              var option = self.getOption(self.items[0]);
              if (option) self.scrollToOption(option, "auto");
            }
          }, 0);
        },
        // Keeps filter highlighting in sync with the current selection.
        onChange: function (value) {
          syncBorder(this, value);
        },
      });
      syncBorder(ts, ts.getValue());
      // Also sync when the native <select> value is changed externally
      // (e.g. Capybara setting the value directly on the hidden element).
      // We also push the change into TomSelect's internal state so that
      // ts.getValue() is never stale (the turbo:before-cache handler relies
      // on it as a fallback when the native element has no selection).
      el.addEventListener("change", () => {
        const val = el.multiple
          ? Array.from(el.selectedOptions, (o) => o.value).filter(
              (v) => v !== "",
            )
          : el.value;
        ts.setValue(val, true); // silent=true: updates internal state without triggering onChange
        syncBorder(ts, val);
      });
      if (savedValues.has(el)) {
        const val = savedValues.get(el);
        ts.setValue(val, true);
        savedValues.delete(el);
        syncBorder(ts, val);
      }
      if (isUserSelect) setupUserSelectToggle(ts, el);
    });
}

// Initialize after every Turbo lifecycle that can introduce new select elements.
document.addEventListener("turbo:load", initTomSelects);
document.addEventListener("turbo:frame-load", initTomSelects);
document.addEventListener("turbo:before-stream-render", () => {
  setTimeout(initTomSelects, 0);
});
